import { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { createPortal } from 'react-dom';
import Markdown from 'react-markdown';
import Documents from '../components/Documents';
import Characteristics from '../components/Characteristics';
import Meldings from '../components/Meldings';
import Cookies from 'js-cookie';
import { Characteristic, Schema, Document, EstimatedCharacteristicsReport, Event, mapCharacteristicsEstimationReport, formatSimulationElapsedSeconds, normalizeCharacteristicAnswer } from '../types';
import { formatAddressReportForDisplay } from '../utils/addressReport';
import { ToastContainer, toast } from 'react-toastify';
import BACKEND_URL from '../config';
import ReconnectingWebSocket from 'reconnecting-websocket';
import ActionView from '../components/ActionView';
import SimulationManager from '../components/SimulationManager';
import { NAVBAR_SLOT_ID } from '../components/VnogNavbar';

const MAX_EVENT_LOG_SIZE = 500;

const SimulationTimer = memo(({ elapsed, status }: { elapsed: string; status: 'connected' | 'reconnecting' | 'disconnected' }) => (
    <>
        <span className="text-xs ml-2 px-2 py-1 bg-gray-100 rounded">
            {elapsed}
        </span>
        <div className="flex items-center gap-1">
            <div className={`w-2 h-2 rounded-full ${
                status === 'connected' ? 'bg-green-500' :
                status === 'reconnecting' ? 'bg-yellow-500' :
                'bg-red-500'
            }`} />
            <span className="text-xs">
                {status === 'connected' ? 'Verbonden' :
                 status === 'reconnecting' ? 'Opnieuw verbinden' :
                 'Niet Verbonden'}
            </span>
        </div>
    </>
));

const Home = () => {
    const [typedSchema, setTypedSchema] = useState<Schema | null>(null);
    const [editableCharacteristics, setEditableCharacteristics] = useState<Record<string, Characteristic>>({});
    const [estimatedCharacteristicsReport, setEstimatedCharacteristicsReport] = useState<EstimatedCharacteristicsReport | null>(null);
    const wsRef = useRef<ReconnectingWebSocket | null>(null);
    const [wsConnectionStatus, setWsConnectionStatus] = useState<'connected' | 'reconnecting' | 'disconnected'>('disconnected');
    const [simulationTimer, setSimulationTimer] = useState<{ elapsed: string; seconds: number }>({ elapsed: '00:00:00', seconds: 0 });
    const [estimationLoading, setEstimationLoading] = useState(false);
    const [documentLoading, setDocumentLoading] = useState(false);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [selectedSimulationToken, setSelectedSimulationToken] = useState<string | null>(null);
    const [simulationLoading, setSimulationLoading] = useState(false);
    const [eventLog, setEventLog] = useState<Event[]>([]);
    const [creatingMessage, setCreatingMessage] = useState(false);
    const [fullscreenSection, setFullscreenSection] = useState<string | null>(null);
    const [showAIActions, setShowAIActions] = useState(false);
    const [operatorsOnly, setOperatorsOnly] = useState(false);
    const [navbarSlot, setNavbarSlot] = useState<HTMLElement | null>(null);

    useEffect(() => {
        setNavbarSlot(document.getElementById(NAVBAR_SLOT_ID));
    }, []);

    const appendEvent = useCallback((newEvent: Event) => {
        setEventLog(prev => {
            const next = [...prev, newEvent];
            return next.length > MAX_EVENT_LOG_SIZE ? next.slice(-MAX_EVENT_LOG_SIZE) : next;
        });
    }, []);

    // Melding tracking ids
    const [selectedCharacteristicsEstimationEventId, setSelectedCharacteristicsEstimationEventId] = useState<number | null>(null);
    const [selectedDocumentationSearchEventId, setSelectedDocumentationSearchEventId] = useState<number | null>(null);
    const [selectedAIOperatorEventId, setSelectedAIOperatorEventId] = useState<number | null>(null);
    const [selectedAIOperatorSummaryEventId, setSelectedAIOperatorSummaryEventId] = useState<number | null>(null);
    const [selectedLastSeenEventId, setSelectedLastSeenEventId] = useState<number | null>(null);
    const [selectedRequestMessageEventId, setSelectedRequestMessageEventId] = useState<number | null>(null);

    const resetAllMeldingTrackingIds = useCallback(() => {
        setSelectedCharacteristicsEstimationEventId(null);
        setSelectedDocumentationSearchEventId(null);
        setSelectedAIOperatorEventId(null);
        setSelectedAIOperatorSummaryEventId(null);
        setSelectedLastSeenEventId(null);
        setSelectedRequestMessageEventId(null);
    }, []);



    // Lazy-load the large characteristics schema (code-split)
    useEffect(() => {
        import('../configuration/characteristics_schema').then(mod => {
            setTypedSchema(mod.default as Schema);
        });
    }, []);

    // Initialize from URL query parameters
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const tokenFromUrl = urlParams.get('simulation');
        if (tokenFromUrl) {
            setSelectedSimulationToken(tokenFromUrl);
        }
    }, []);

    // Update URL and persist last simulation token when it changes
    useEffect(() => {
        const url = new URL(window.location.href);
        if (selectedSimulationToken) {
            url.searchParams.set('simulation', selectedSimulationToken);
            localStorage.setItem('vnog_last_simulation', selectedSimulationToken);
        } else {
            url.searchParams.delete('simulation');
        }
        window.history.replaceState({}, '', url.toString());
    }, [selectedSimulationToken]);

    const [showActionView, setShowActionView] = useState(false);
    const [coCommanderKilled, setCoCommanderKilled] = useState(false);
    const [summary, setSummary] = useState('');
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [documentRecommendationsLoading, setDocumentRecommendationsLoading] = useState(false);

    const resetAll = useCallback(() => {
        setInitialCharacteristics();
        setEstimatedCharacteristicsReport(null);
        setDocuments([]);
        setEventLog([]);
        setEstimationLoading(false);
        setSimulationTimer({ elapsed: '00:00:00', seconds: 0 });
    }, []);
    // WebSocket connection management
    useEffect(() => {
        if (selectedSimulationToken) {
            // Close existing connection if any
            if (wsRef.current) {
                wsRef.current.close();
            }

            // Create new WebSocket connection
            const wsUrl = `${BACKEND_URL.replace('http', 'ws')}/ws/simulation/${selectedSimulationToken}/`;
            const ws = new ReconnectingWebSocket(wsUrl);
            wsRef.current = ws;

            ws.onopen = () => {
                setWsConnectionStatus('connected');
            };

            ws.onclose = () => {
                setWsConnectionStatus('disconnected');
            };

            ws.onmessage = (event) => {
                const data = JSON.parse(event.data);
                if (data.type === 'characteristics') {
                    toast.success('Updated Characteristics');
                    appendEvent(data.data);
                    setEstimationLoading(false);
                    setSelectedCharacteristicsEstimationEventId(data.data.id);
                    setSelectedAIOperatorEventId(null);
                    setEstimatedCharacteristicsReport(
                        mapCharacteristicsEstimationReport(
                            data.data.id,
                            data.data.characteristics_estimation,
                        ),
                    );
                } else if (data.type === 'message') {
                    appendEvent(data.data);
                }
                else if (data.type === 'documents') {
                    setDocumentLoading(false);
                    setSelectedDocumentationSearchEventId(data.event.id);
                    setSelectedAIOperatorEventId(null);
                    appendEvent(data.event);
                    setDocuments(data.data);
                } else if (data.type === 'ai_summary') {
                    appendEvent(data.data);
                } else if (data.type === 'document_recommendations') {
                    appendEvent(data.data);
                    setDocumentRecommendationsLoading(false);
                } else if (data.type === 'timer') {
                    if (data.data && typeof data.data.elapsed === 'string') {
                        setSimulationTimer({
                            elapsed: data.data.elapsed,
                            seconds: typeof data.data.seconds === 'number' ? data.data.seconds : 0,
                        });
                    }
                } else if (data.type === 'loading_update') {
                    if (data.data && data.data.type === 'characteristics') {
                        setEstimationLoading(data.data.loading);
                    } else if (data.data && data.data.type === 'documents') {
                        setDocumentLoading(data.data.loading);
                    }
                }
            };

            ws.onerror = () => {
                toast.error('Error connecting to situation updates');
                setWsConnectionStatus('reconnecting');
            };

            return () => {
                if (wsRef.current) {
                    wsRef.current.close();
                    wsRef.current = null;
                }
            };
        }
    }, [selectedSimulationToken]);

    // Initialize with schema when it loads
    const setInitialCharacteristics = () => {
        if (!typedSchema) return;
        const initialCharacteristics: Record<string, {
            answer: string;
            explanation: string;
            confidence: number | null;
            importance: number | null;
            isEdited: boolean;
            corrected_by?: string;
            comment?: string;
        }> = {};
        Object.keys(typedSchema).forEach(key => {
            initialCharacteristics[key] = {
                answer: '',
                explanation: '',
                confidence: null,
                importance: null,
                isEdited: false
            };
        });
        setEditableCharacteristics(initialCharacteristics);
    }
    useEffect(() => {
        setInitialCharacteristics();
    }, [typedSchema]);


    const getCharacteristicsEstimation = useCallback((characteristicsEstimationEventId: number) => {
        setEstimationLoading(true);
        fetch(`${BACKEND_URL}/vnog/get_characteristics_estimation/${characteristicsEstimationEventId}/`, {
            method: 'GET',
            credentials: 'include',
        })
        .then(response => response.json())
        .then(data => {
            setEstimatedCharacteristicsReport(
                mapCharacteristicsEstimationReport(characteristicsEstimationEventId, data),
            );
            setEstimationLoading(false);
        })
        .catch(() => {})
    }, []);



    const estimateCharacteristics = useCallback(() => {
        if (!selectedSimulationToken) {
            toast.error('No simulation token selected');
            return;
        }
        setEstimationLoading(true);
        fetch(`${BACKEND_URL}/vnog/estimate_characteristics/`, {
            method: 'POST',
            body: new URLSearchParams({
                'simulation_token': selectedSimulationToken || ''
            }),
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'X-CSRFToken': Cookies.get('csrftoken') || '',
            },
            credentials: 'include',
        })
        .then(response => response.json())
    }, [selectedSimulationToken]);

    // When new characteristics are estimated, update the editable characteristics
    useEffect(() => {
        if (estimatedCharacteristicsReport && estimatedCharacteristicsReport.characteristics && typedSchema) {
            const newCharacteristics: Record<string, {
                answer: string;
                explanation: string;
                confidence: number | null;
                importance: number | null;
                isEdited: boolean;
                corrected_by?: string;
                comment?: string;
            }> = {};
            
            // Convert the received characteristics and merge with schema.
            // The backend (DB) is the source of truth for which characteristics
            // exist, so union the schema keys with the keys actually estimated.
            // This ensures characteristics that aren't in the bundled schema
            // (e.g. the role `<RoleSlug>_phase` characteristics) still display.
            const characteristicKeys = new Set<string>([
                ...Object.keys(typedSchema),
                ...Object.keys(estimatedCharacteristicsReport.characteristics),
            ]);
            characteristicKeys.forEach(key => {
                if (estimatedCharacteristicsReport.characteristics[key]) {
                    // If the characteristic exists in the report
                    const char = estimatedCharacteristicsReport.characteristics[key];

                    newCharacteristics[key] = {
                        answer: normalizeCharacteristicAnswer(char.answer),
                        explanation: char.explanation,
                        confidence: char.confidence,
                        importance: char.importance,
                        isEdited: false,
                        corrected_by: char.corrected_by,
                        comment: char.comment
                    };
                } else {
                    // If the characteristic doesn't exist in the report
                    newCharacteristics[key] = {
                        answer: '',
                        explanation: '',
                        confidence: null,
                        importance: null,
                        isEdited: false,
                        corrected_by: undefined,
                        comment: undefined
                    };
                }
            });
            
            setEditableCharacteristics(newCharacteristics);
        }
    }, [estimatedCharacteristicsReport, typedSchema]);

    const reportMarkdown = useMemo(
        () => formatAddressReportForDisplay(estimatedCharacteristicsReport?.raw_input),
        [estimatedCharacteristicsReport?.raw_input],
    );

    const FullscreenButton = ({ sectionId }: { sectionId: string }) => (
        <button
            onClick={() => setFullscreenSection(fullscreenSection === sectionId ? null : sectionId)}
            className="absolute top-2 right-2 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
            title={fullscreenSection === sectionId ? "Exit fullscreen" : "Enter fullscreen"}
        >
            {fullscreenSection === sectionId ? "✕" : "⛶"}
        </button>
    );


    const getDocumentsFromCharacteristic = useCallback(() => {
        setDocumentLoading(true);
        if (!selectedSimulationToken) {
            toast.error('No simulation token selected');
            return;
        }
        fetch(`${BACKEND_URL}/vnog/get_documents_from_characteristic/`, {
            method: 'POST',
            body: new URLSearchParams({
                'simulation_token': selectedSimulationToken || ''
            }),
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'X-CSRFToken': Cookies.get('csrftoken') || '',
            },
            credentials: 'include',
        })
            .then(response => response.json())
            .catch(() => {})
    }, [selectedSimulationToken]);

    const getDocumentsFromDocumentationSearch = useCallback((documentationSearchEventId: number) => {
        setDocumentLoading(true);
        fetch(`${BACKEND_URL}/vnog/get_documents_from_documentation_search/${documentationSearchEventId}/`, {
            method: 'GET',
            credentials: 'include',
        })
        .then(response => response.json())
        .then(data => {
            setDocumentLoading(false);
            setDocuments(data);
        })
        .catch(() => {
            setDocumentLoading(false);
        })
    }, []);

    // Hydrate event log from simulation detail loaded by SimulationManager (deduplicates the fetch)
    const handleSimulationDetailLoaded = useCallback((detail: Record<string, unknown>) => {
        if (typeof detail?.seconds_elapsed === 'number') {
            setSimulationTimer({
                elapsed: formatSimulationElapsedSeconds(detail.seconds_elapsed),
                seconds: detail.seconds_elapsed,
            });
        }
        if (detail && Array.isArray(detail.event_log)) {
            setEventLog(detail.event_log);
            const lastCharEvent = [...(detail.event_log as Event[])].reverse().find((e) => e.event_type === 'characteristics_estimation');
            if (lastCharEvent && lastCharEvent.id) {
                setSelectedCharacteristicsEstimationEventId(lastCharEvent.id);
                getCharacteristicsEstimation(lastCharEvent.id);
            }

            const lastDocSearchEvent = [...(detail.event_log as Event[])].reverse().find((e) => e.event_type === 'documentation_search');
            if (lastDocSearchEvent && lastDocSearchEvent.id) {
                setSelectedDocumentationSearchEventId(lastDocSearchEvent.id);
                getDocumentsFromDocumentationSearch(lastDocSearchEvent.id);
            }
        }
    }, [getCharacteristicsEstimation, getDocumentsFromDocumentationSearch]);

    const setSelectedCharacteristicsEstimation = useCallback((characteristicsEstimationEventId: number) => {
        setSelectedCharacteristicsEstimationEventId(characteristicsEstimationEventId);
        getCharacteristicsEstimation(characteristicsEstimationEventId);
    }, [getCharacteristicsEstimation]);

    const setSelectedDocumentationSearch = useCallback((documentationSearchEventId: number) => {
        setSelectedDocumentationSearchEventId(documentationSearchEventId);
        getDocumentsFromDocumentationSearch(documentationSearchEventId);
    }, [getDocumentsFromDocumentationSearch]);

    const selectAIOperatorEvent = useCallback((aiOperatorEventId: number, characteristicsId?: number, documentationId?: number) => {
        resetAllMeldingTrackingIds();
        setSelectedAIOperatorEventId(aiOperatorEventId);
        if (typeof characteristicsId === 'number') {
            setSelectedCharacteristicsEstimation(characteristicsId);
        }
        if (typeof documentationId === 'number') {
            setSelectedDocumentationSearch(documentationId);
        }
    }, [resetAllMeldingTrackingIds, setSelectedCharacteristicsEstimation, setSelectedDocumentationSearch]);

    const createMessage = useCallback(async (message: string): Promise<boolean> => {
        if (!selectedSimulationToken) return false;
        setCreatingMessage(true);
        try {
            const resp = await fetch(`${BACKEND_URL}/vnog/add_message_to_simulation/`, {
                method: 'POST',
                body: new URLSearchParams({
                    'simulation_token': selectedSimulationToken || '',
                    'message': message
                }),
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                },
                credentials: 'include',
            });
            setCreatingMessage(false);
            if (!resp.ok) {
                return false;
            }
            // Success path: we wait for websocket 'message' echo to append and clear loading
            return true;
        } catch (_) {
            setCreatingMessage(false);
            return false;
        }
    }, [selectedSimulationToken]);

    const onEventCreated = useCallback((newEvent: Event) => appendEvent(newEvent), [appendEvent]);

    if (!typedSchema) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="h-10 w-10 rounded-full border-4 border-gray-300 border-t-transparent animate-spin" />
            </div>
        );
    }

    return (
        <div className="p-2">
            <ToastContainer />
            {navbarSlot && createPortal(
                <div className="flex items-center gap-4 text-sm">
                    {selectedSimulationToken && (
                        <>
                            <span className="text-gray-500">
                                Simulatie: <span className="font-mono font-medium text-gray-800">{selectedSimulationToken}</span>
                            </span>
                            <SimulationTimer elapsed={simulationTimer.elapsed} status={wsConnectionStatus} />
                        </>
                    )}
                    <ActionView 
                        summary={summary} 
                        eventLog={eventLog} 
                        showActionView={showActionView} 
                        setShowActionView={setShowActionView}
                        setEventLog={setEventLog}
                        simulationToken={selectedSimulationToken}
                        createMessage={createMessage}
                        coCommanderKilled={coCommanderKilled}
                        setCoCommanderKilled={setCoCommanderKilled}
                        kenmerkenschema={estimatedCharacteristicsReport?.kenmerkenschema ?? null}
                        pager={estimatedCharacteristicsReport?.pager ?? null}
                    />
                </div>,
                navbarSlot
            )}
            {fullscreenSection && fullscreenSection !== 'simulation' && (
                <div className="fixed inset-0 z-50 bg-gray-50 p-4">
                    {fullscreenSection === 'characteristics' && (
                        <div className="flex flex-col h-full bg-white rounded-lg shadow-md p-4 relative overflow-hidden">
                            <FullscreenButton sectionId="characteristics" />
                            <Characteristics 
                                editableCharacteristics={editableCharacteristics} 
                                setEditableCharacteristics={setEditableCharacteristics} 
                                typedSchema={typedSchema}
                                estimateCharacteristics={estimateCharacteristics}
                                estimatedCharacteristicsReport={estimatedCharacteristicsReport}
                                estimationLoading={estimationLoading}
                                simulation_token={selectedSimulationToken}
                                isFullscreen={true}
                            />
                        </div>
                    )}
                    {fullscreenSection === 'meldings' && (
                        <div className="flex flex-col h-full bg-white rounded-lg shadow-md p-4 relative overflow-hidden">
                            <FullscreenButton sectionId="meldings" />
                            <Meldings 
                            eventLog={eventLog} 
                            createMessage={createMessage} 
                            creatingMessage={creatingMessage}
                            setSelectedCharacteristicsEstimationEventId={setSelectedCharacteristicsEstimation}
                            setSelectedDocumentationSearchEventId={setSelectedDocumentationSearch}
                            selectedAIOperatorEventId={selectedAIOperatorEventId}
                            setSelectedAIOperatorEventId={setSelectedAIOperatorEventId}
                            selectAIOperatorEvent={selectAIOperatorEvent}
                            selectedAIOperatorSummaryEventId={selectedAIOperatorSummaryEventId}
                            setSelectedAIOperatorSummaryEventId={setSelectedAIOperatorSummaryEventId}
                            selectedRequestMessageEventId={selectedRequestMessageEventId}
        					selectedLastSeenEventId={selectedLastSeenEventId}
        					setSelectedRequestMessageEventId={setSelectedRequestMessageEventId}
        					setSelectedLastSeenEventId={setSelectedLastSeenEventId}
                            resetAllMeldingTrackingIds={resetAllMeldingTrackingIds}
                            selectedCharacteristicsEstimationEventId={selectedCharacteristicsEstimationEventId} 
                            selectedDocumentationSearchEventId={selectedDocumentationSearchEventId}
                            isFullscreen={true}
                            simulationToken={selectedSimulationToken}
                            onEventCreated={onEventCreated}
                            showAIActions={showAIActions}
                            setShowAIActions={setShowAIActions}
                            operatorsOnly={operatorsOnly}
                            setOperatorsOnly={setOperatorsOnly} />
                        </div>
                    )}
                    {fullscreenSection === 'documents' && (
                        <div className="flex flex-col h-full bg-white rounded-lg shadow-md p-4 relative overflow-hidden">
                            <FullscreenButton sectionId="documents" />
                            <Documents loading={documentLoading} documents={documents} getDocumentsFromCharacteristic={getDocumentsFromCharacteristic} isFullscreen={true} />
                        </div>
                    )}
                </div>
            )}
            <div className={`flex flex-row justify-between pt-1 gap-2 ${fullscreenSection === 'simulation' ? 'fixed inset-0 z-50 bg-gray-50 p-4' : ''}`}>
                <div className={`flex flex-col ${fullscreenSection === 'simulation' ? 'w-full' : 'w-1/2'} gap-2`}>
                    <div className={`flex flex-row gap-6 ${fullscreenSection === 'simulation' ? 'h-full' : 'h-[50vh] lg:h-[45vh]'} justify-start bg-white rounded-lg shadow-md p-4 relative`}>
                        <FullscreenButton sectionId="simulation" />
                        <div className="flex-1 min-w-0">
                            <SimulationManager
                                setSimulationLoading={setSimulationLoading}
                                setSimulationToken={setSelectedSimulationToken}
                                resetAll={resetAll}
                                selectedToken={selectedSimulationToken}
                                onSimulationDetailLoaded={handleSimulationDetailLoaded}
                                pagerSnapshot={estimatedCharacteristicsReport?.pager ?? null}
                                simulationElapsed={simulationTimer.elapsed}
                            />
                        </div>
                    </div>
                    {!fullscreenSection && (
                            <>
                                <div className="flex flex-col h-[75vh] lg:h-[85vh] bg-white rounded-lg shadow-md p-4 relative">  
                                    <FullscreenButton sectionId="characteristics" />
                                    <Characteristics 
                                        editableCharacteristics={editableCharacteristics} 
                                        setEditableCharacteristics={setEditableCharacteristics} 
                                        typedSchema={typedSchema}
                                        estimateCharacteristics={estimateCharacteristics}
                                        estimatedCharacteristicsReport={estimatedCharacteristicsReport}
                                        estimationLoading={estimationLoading}
                                        simulation_token={selectedSimulationToken}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                    {!fullscreenSection && (
                        <div className="flex flex-col w-1/2 gap-2">
                            <div className="flex flex-col h-[65vh] lg:h-[60vh] bg-white rounded-lg shadow-md p-4 relative">
                                <FullscreenButton sectionId="meldings" />
                                <Meldings 
                                eventLog={eventLog} 
                                createMessage={createMessage} 
                                creatingMessage={creatingMessage}
                                setSelectedCharacteristicsEstimationEventId={setSelectedCharacteristicsEstimation}
                                setSelectedDocumentationSearchEventId={setSelectedDocumentationSearch}
                                selectedAIOperatorEventId={selectedAIOperatorEventId}
                                setSelectedAIOperatorEventId={setSelectedAIOperatorEventId}
                                selectAIOperatorEvent={selectAIOperatorEvent}
                                selectedAIOperatorSummaryEventId={selectedAIOperatorSummaryEventId}
                                setSelectedAIOperatorSummaryEventId={setSelectedAIOperatorSummaryEventId}
                                selectedRequestMessageEventId={selectedRequestMessageEventId}
            					selectedLastSeenEventId={selectedLastSeenEventId}
            					setSelectedRequestMessageEventId={setSelectedRequestMessageEventId}
            					setSelectedLastSeenEventId={setSelectedLastSeenEventId}
                                resetAllMeldingTrackingIds={resetAllMeldingTrackingIds}
                                selectedCharacteristicsEstimationEventId={selectedCharacteristicsEstimationEventId} 
                                selectedDocumentationSearchEventId={selectedDocumentationSearchEventId}
                                simulationToken={selectedSimulationToken}
                                onEventCreated={onEventCreated}
                                showAIActions={showAIActions}
                                setShowAIActions={setShowAIActions}
                                operatorsOnly={operatorsOnly}
                                setOperatorsOnly={setOperatorsOnly} />
                            </div>
                            <div className="flex flex-col bg-white rounded-lg shadow-md p-4 relative">
                                <FullscreenButton sectionId="documents" />
                                <Documents loading={documentLoading} documents={documents} getDocumentsFromCharacteristic={getDocumentsFromCharacteristic} />
                            </div>
                            {estimatedCharacteristicsReport && reportMarkdown && (
                                <div className="flex flex-col h-[50vh] lg:h-[45vh] bg-white rounded-lg shadow-md p-4 relative min-h-0">
                                    <h3 className="text-base font-semibold text-gray-900 mb-2 flex-shrink-0">Report</h3>
                                    <div className="flex-1 min-h-0 overflow-y-auto pr-1">
                                        <div className="prose prose-sm max-w-none text-gray-700">
                                            <Markdown>{reportMarkdown}</Markdown>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            {simulationLoading && (
                <div className="fixed inset-0 z-50 bg-gray-500/40 flex items-center justify-center">
                    <div className="h-14 w-14 rounded-full border-4 border-white/60 border-t-transparent animate-spin" />
                </div>
            )}
        </div>
    );
}

export default Home;